import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, User, Lock, Phone, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFlashMessage } from '../components/FlashMessage';
import { usersAPI, handleApiError } from '../services/api';
import { formatPhoneNumber, cleanPhoneNumber } from '../utils/phoneFormatter';
import { FormSelect } from '../components/FormSelect';

const OwnerNewUserForm = () => {
  const navigate = useNavigate();
  const { getUserLocations } = useAuth();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [isLoading, setIsLoading] = useState(false);

  const locations = getUserLocations();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    phone: '',
    full_name: '',
    location_id: '',
    role: 'staff'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (locations.length === 1) {
      setFormData(prev => ({ ...prev, location_id: locations[0].id }));
    }
  }, [locations]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      const formattedValue = formatPhoneNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Kullanıcı adı gereklidir';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Kullanıcı adı gereklidir';
    }

    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    }

    if (!formData.location_id) {
      newErrors.location_id = 'Lokasyon seçimi gereklidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showDanger('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    setIsLoading(true);

    try {
      const userData = {
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name,
        phone: cleanPhoneNumber(formData.phone) || null,
        locations: [{
          location_id: parseInt(formData.location_id),
          role: formData.role
        }]
      };

      await usersAPI.createUser(userData);

      showSuccess('Kullanıcı başarıyla oluşturuldu');

      // Navigate back to users list after short delay
      setTimeout(() => {
        navigate('/owner/users');
      }, 1000);

    } catch (error) {
      console.error('Create user error:', error);
      showDanger(handleApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/owner/users')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Yeni Kullanıcı Ekle</h1>
            <p className="text-sm text-gray-600 mt-1">Yeni vale kullanıcısı oluşturun</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Kullanıcı Adı */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="inline h-4 w-4 mr-1" />
                  Kullanıcı Adı *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Kullanıcı adı"
                  className={`form-input ${errors.full_name ? 'border-red-500' : ''}`}
                />
                {errors.full_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>
                )}
              </div>

              {/* Ad Soyad (username) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ad Soyad *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Ad Soyad"
                  className={`form-input ${errors.username ? 'border-red-500' : ''}`}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username}</p>
                )}
              </div>
            </div>

            {/* Telefon ve Şifre */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Telefon (İsteğe bağlı)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Telefon numarası"
                  className="form-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Lock className="inline h-4 w-4 mr-1" />
                  Şifre *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Şifre"
                  className={`form-input ${errors.password ? 'border-red-500' : ''}`}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
            </div>

            {/* Lokasyon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="inline h-4 w-4 mr-1" />
                Lokasyon *
              </label>
              <FormSelect
                name="location_id"
                value={formData.location_id}
                onChange={handleInputChange}
                options={locations.map(location => ({ value: location.id.toString(), label: location.name }))}
                placeholder="Lokasyon seçin"
                error={errors.location_id}
              />
              {errors.location_id && (
                <p className="mt-1 text-sm text-red-600">{errors.location_id}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/owner/users')}
                className="btn-secondary"
                disabled={isLoading}
              >
                İptal
              </button>
              <button
                type="submit"
                className="btn-primary flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Kullanıcı Ekle
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OwnerNewUserForm;
